// The standard quick filter buttons — On-Track Technologies (2026-09-29).
//
// Every dashboard custom in this fork carries admin-defined quick filter
// buttons: a caption, a ZQL expression, and whether the button is pressed
// when the page opens. Each page keeps that list in its own corner of the
// page config (`filterButtons`, `ncr.buttons`, `capacity.buttons`, …) and
// resolves a pressed button to a key set with one keys-only call inside
// the page's own scope. This module is the ONE list of buttons the client
// wants on EVERY dashboard, and the one rule that puts them there:
//
//   withStandardButtons(saved)  →  the saved buttons, in their saved order,
//                                  followed by every standard button the
//                                  saved list does not already carry.
//
// "Already carries" is decided by caption OR by ZQL (both compared loosely:
// case, spacing and outer parentheses ignored), never by id — an admin who
// typed "CNC Tasks" with the same ZQL before this list existed keeps their
// button and does not get a twin. The standard buttons are all OFF when the
// page opens; an admin can press one, or flip its "on by default" flag in
// the page's settings, and Save then persists the whole list — after which
// the merge is a no-op because every caption is present.
//
// A standard button an admin removes in the settings comes back on the next
// open (it is the standard). To retire one for every page, delete it here.
//
// WHY THIS FILE SITS AT customs/ AND NOT customs/shared/: the build puts
// every directory under customs/ in its own chunk, and Work Allocations
// (customs/WorkAllocations.jsx, no directory) is bundled with the app's
// entry. An entry-level import of customs/shared/ made the entry load the
// custom-shared chunk first, rollup moved React into it, and the live site
// went blank on 2026-09-29 ("reading __SECRET_INTERNALS"). A root-level
// file is bundled with the entry, which is the safe direction.

export const STANDARD_BUTTONS = Object.freeze([
  { id: 'std-cnc-projects', caption: 'CNC Projects', zql: '(category = "CNC Projects")', on: false },
  { id: 'std-cnc-tasks', caption: 'CNC Tasks', zql: '(issuetype in ("CNC-Processes", "CNC Code", "Machine Operation", "CNC Prepping"))', on: false },
  { id: 'std-technical-tasks', caption: 'Technical Tasks', zql: '(issuetype IN (Assembly, "CNC-Processes", Composites, "Manual Fabrication", "CNC Code", Trimming, Lay-Up, "Machine Operation"))', on: false },
  { id: 'std-project-tasks', caption: 'Project Tasks', zql: '(issuetype not in (Design, "Design Sub-task", "Draughting Sub-task", Draughting, "Engineering Support", "RFC Draughting", Assembly, "CNC-Processes", Composites, "Manual Fabrication", "CNC Code", Trimming, Lay-Up, "Machine Operation"))', on: false },
  { id: 'std-engineering-tasks', caption: 'Engineering Tasks', zql: '(issuetype in (Design, "Design Sub-task", "Draughting Sub-task", Draughting, "Engineering Support", "RFC Draughting"))', on: false },
].map(Object.freeze));

// Loose comparison: case, runs of whitespace and one pair of outer
// parentheses do not make two captions or two ZQL texts different.
export const looseKey = (s) => {
  let t = String(s == null ? '' : s).toLowerCase().replace(/\s+/g, ' ').trim();
  while (t.startsWith('(') && t.endsWith(')') && balanced(t.slice(1, -1))) t = t.slice(1, -1).trim();
  return t;
};
const balanced = (s) => { let d = 0; for (const ch of s) { if (ch === '(') d += 1; else if (ch === ')') { d -= 1; if (d < 0) return false; } } return d === 0; };

// The merge. `saved` is whatever the page normalised from its config (any
// array of { id, caption, zql, … }); the result keeps those objects as they
// are and appends a fresh copy of each missing standard button. `shape` lets
// a page whose buttons carry no `on` flag (or extra keys) receive the
// standard ones in its own shape.
export function withStandardButtons(saved, shape = (b) => ({ ...b })) {
  const list = Array.isArray(saved) ? saved.filter(Boolean) : [];
  const captions = new Set(list.map((b) => looseKey(b.caption)).filter(Boolean));
  const zqls = new Set(list.map((b) => looseKey(b.zql)).filter(Boolean));
  const ids = new Set(list.map((b) => String(b.id || '')));
  const missing = STANDARD_BUTTONS.filter((b) => !captions.has(looseKey(b.caption)) && !zqls.has(looseKey(b.zql)) && !ids.has(b.id));
  if (!missing.length) return Array.isArray(saved) && list.length === saved.length ? saved : list;   // nothing to add → the same array (a memo stays warm)
  return [...list, ...missing.map((b) => shape({ ...b }))];
}
